/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {TimepickerModule} from 'ngx-bootstrap/timepicker';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import template from './data-export-edit-page.component.html';

import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {AdnSelectSearchElement} from '../../common/directives/adn-select/adn-select.component';
import {networkTimeFromPickerDate, pickerDateFromNetworkTime} from '../../common/locale/network-date-picker-utils';
import {TeamApiService} from '../team/team-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {SearchApiService} from '../../common/controller/search-api.service';
import {DataExportApiService, DataExportModel} from './data-export-api.service';
import {AppSharedModule} from '../../common/app-shared.module';
import {AdnSelectComponent} from '../../common/directives/adn-select/adn-select.component';
import {LineItemLabelsModule} from '../../line-item/line-item-labels.module';

interface DataExportField {
  name?: string;
  source?: string;
  constantString?: string;
  constantNumber?: number;
  editable?: boolean;
}

const BASE_STATS_FIELDS = [
  'Start',
  'StartDate',
  'Publisher',
  'Publisher_Id',
  'Site',
  'Site_Id',
  'Ad_Unit',
  'Ad_Unit_Id',
  'Ad_Unit_Labels',
  'Advertiser',
  'Advertiser_Id',
  'Order',
  'OrderName',
  'Order_Id',
  'Order_Team',
  'Order_Team_Id',
  'Line_Item',
  'Line_Item_Id',
  'Political',
  'Creative',
  'Creative_Id',
  'Creative_Source',
  'Creative_Size',
  'Available_Traffic',
  'Losing_Bids',
  'Impressions',
  'Rendered_Impressions',
  'Visibles',
  'Visibility',
  'Viewables',
  'Viewability',
  'Clicks',
  'Conversions',
  'CTR',
  'Currency',
  'eCPM',
  'vCPM',
  'Cost',
  'Total_Cost'
];

const AWS_REGIONS = [
  'EU_CENTRAL_1',
  'EU_CENTRAL_2',
  'EU_NORTH_1',
  'EU_SOUTH_1',
  'EU_SOUTH_2',
  'AP_EAST_1',
  'AP_SOUTH_1',
  'AP_SOUTH_2',
  'AP_SOUTHEAST_1',
  'AP_SOUTHEAST_2',
  'AP_SOUTHEAST_3',
  'AP_SOUTHEAST_4',
  'AP_NORTHEAST_1',
  'AP_NORTHEAST_2',
  'AP_NORTHEAST_3',
  'SA_EAST_1',
  'CN_NORTH_1',
  'CN_NORTHWEST_1',
  'CA_CENTRAL_1',
  'CA_WEST_1',
  'GovCloud',
  'US_GOV_EAST_1',
  'US_EAST_1',
  'US_EAST_2',
  'US_WEST_1',
  'US_WEST_2',
  'EU_WEST_1',
  'EU_WEST_2',
  'EU_WEST_3',
  'ME_CENTRAL_1',
  'ME_SOUTH_1',
  'AF_SOUTH_1',
  'US_ISO_EAST_1',
  'US_ISOB_EAST_1',
  'US_ISO_WEST_1',
  'IL_CENTRAL_1'
];

const EVENT_TYPES = [
  {id: 'IMPRESSION', transKey: 'impression'},
  {id: 'IN_BROWSER', transKey: 'rendered'},
  {id: 'VISIBLE', transKey: 'Visible Impressions'},
  {id: 'VIEWABLE', transKey: 'Viewable Impressions'},
  {id: 'CLICK', transKey: 'Clicks'},
  {id: 'CUSTOM', transKey: 'Conversions'}
];

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, UIRouterModule, ButtonsModule, TimepickerModule, AdnSelectComponent, LineItemLabelsModule],
  selector: 'adn-data-export-edit-page',
  template
})
export class DataExportEditPageComponent extends SignalAdminResourceEditPageBase<DataExportModel> {
  readonly allEventTypes = EVENT_TYPES;
  readonly awsRegions = AWS_REGIONS;

  app: string;
  perms: any;
  currencies: string[] = [];
  statsFields: string[] = [];
  fields: Record<string, DataExportField[]> = {};
  allTeams: any[] = [];
  teamsLoaded = false;
  advertiserSearchResults: any[] = [];
  orderSearchResults: any[] = [];
  eventTypeOption: 'ALL' | 'SELECTIVE' = 'ALL';
  macroHelp = false;
  exampleExport = false;
  private lastLoadedStatsFilterType?: string;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly searchApi: SearchApiService,
    private readonly ngZone: NgZone
  ) {
    super(inject(DataExportApiService), 'dataExport');
    this.app = this.localUserProfile.getCurrentApp();
    this.perms = this.LocalUserPermissions.getAllVisPermissions();
    this.currencies = this.LocalNetworkProfile.get('availableCurrencies') || [];
  }

  protected override afterEditingModelReset(): void {
    this.statsFields = this.buildStatsFields();
    this.initializeNestedSettings();
    this.initializeScheduleTime();

    if (this.isNew) {
      this.initializeDefaultsForNewModel();
    }

    if (this.editingModel.source === 'STATS') {
      this.editingModel.type = this.editingModel.type || 'SHEET';
      if (this.editingModel.statsSettings.filterType === 'TEAM') {
        this.updateAllTeams();
        this.editingModel.statsSettings.teams = this.toIdArray(this.editingModel.statsSettings.teams);
      }
    } else if (this.editingModel.source === 'RAW_LOG') {
      this.updateAllTeams();
    }

    this.editingModel.teams = this.toIdArray(this.editingModel.teams);
    this.editingModel.example = this.parseExample(this.editingModel.example);
    this.doFields();
    this.eventTypeOption = !this.editingModel.eventTypes || this.editingModel.eventTypes.length === 0 ? 'ALL' : 'SELECTIVE';
  }

  protected override prepareForSubmit(model: DataExportModel): void {
    const timeZone = this.LocalNetworkProfile.get('timeZone') || 'utc';
    model.scheduleTime = _.isDate(model.modScheduleTime)
      ? networkTimeFromPickerDate(model.modScheduleTime, timeZone)
      : null;

    model.fields = _.map(_.filter(this.fields[model.source], (field: DataExportField) => {
      return field.name && field.source;
    }), (field: DataExportField) => {
      delete field.editable;
      return field;
    });

    if (this.eventTypeOption === 'ALL') {
      model.eventTypes = [];
    }
  }

  isVisible(source: string): boolean {
    switch (source) {
      case 'SEGMENT_DEFINITIONS':
      case 'USER_SEGMENTS':
      case 'USER_PROFILES':
        return this.app === 'DATA';
      case 'RAW_LOG':
        return this.app === 'ADVERTISING' && this.perms.DOWNLOAD_LOGS === true;
      case 'STATS':
        return this.app === 'ADVERTISING'
          && (this.perms.AX_ADVERTISER_TEAM_ADMIN === true || this.perms.AX_PUBLISHER_TEAM_ADMIN === true || this.perms.AX_ADVERTISER_ADMIN === true || this.perms.AX_PUBLISHER_ADMIN === true || this.perms.AX_PUBLISHER_ADMIN === true || this.perms.MANAGE_SYSTEM === true);
      default:
        return false;
    }
  }

  advertiserSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchApi.search({q: searchElement.search, types: 'Advertiser'}).subscribe((data) => {
      this.ngZone.run(() => {
        this.advertiserSearchResults = data.searchResults;
      });
    });
  };

  orderSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchApi.search({q: searchElement.search, types: 'Order'}).subscribe((data) => {
      this.ngZone.run(() => {
        this.orderSearchResults = data.searchResults;
      });
    });
  };

  updateStatsFilterType(): void {
    const previousFilterType = this.lastLoadedStatsFilterType;
    switch (this.editingModel.statsSettings.filterType) {
      case 'TEAM':
        if (previousFilterType !== 'TEAM') {
          this.updateAllTeams();
        }
        this.editingModel.statsSettings.advertisers = [];
        this.editingModel.statsSettings.orders = [];
        break;
      case 'ADVERTISER':
        this.editingModel.statsSettings.teams = [];
        this.editingModel.statsSettings.orders = [];
        break;
      case 'ORDER':
        this.editingModel.statsSettings.teams = [];
        this.editingModel.statsSettings.advertisers = [];
        break;
      default:
        this.editingModel.statsSettings.filterType = undefined;
        this.editingModel.statsSettings.teams = [];
        this.editingModel.statsSettings.advertisers = [];
        this.editingModel.statsSettings.orders = [];
        break;
    }
    this.lastLoadedStatsFilterType = this.editingModel.statsSettings.filterType;
    this.markExternalFormDirty();
  }

  updateLineItemCurrency(): void {
    this.editingModel.statsSettings.currency = null;
    this.markExternalFormDirty();
  }

  updateDestination(): void {
    switch (this.editingModel.type) {
      case 'AZURE_STORAGE_BLOB':
        this.editingModel.azureSettings.environment = this.editingModel.azureSettings.environment || 'AZURE';
        break;
      case 'S3':
        this.editingModel.awsSettings.region = this.editingModel.awsSettings.region || 'EU_CENTRAL_1';
        break;
      case 'S3_COMPATIBLE_BUCKET':
        this.editingModel.awsSettings.region = undefined;
        break;
      default:
    }
    this.markExternalFormDirty();
  }

  updateSource(): void {
    switch (this.editingModel.source) {
      case 'SEGMENT_DEFINITIONS':
      case 'USER_SEGMENTS':
      case 'USER_PROFILES':
        if (this.editingModel.type === 'S3') {
          this.editingModel.type = 'SFTP';
        }
        break;
      case 'RAW_LOG':
        this.editingModel.type = 'S3';
        if (this.editingModel.schedule === 'WEEKLY') {
          this.editingModel.schedule = 'DAILY';
        }
        this.updateAllTeams();
        break;
      case 'STATS':
        this.editingModel.type = 'SHEET';
        this.editingModel.data = 'DELTAS';
        this.editingModel.schedule = 'DAILY';
        if (this.editingModel.statsSettings.filterType === 'TEAM') {
          this.updateAllTeams();
        }
        break;
      default:
    }
    this.doFields();
    this.markExternalFormDirty();
  }

  setEventTypeOption(option: 'ALL' | 'SELECTIVE'): void {
    this.eventTypeOption = option;
    if (option === 'ALL') {
      this.editingModel.eventTypes = [];
    } else {
      this.editingModel.eventTypes = this.editingModel.eventTypes || [];
    }
    this.markExternalFormDirty();
  }

  addField(): void {
    this.doFields();
    const defaultField: DataExportField = {editable: true};
    if (this.editingModel.source === 'SEGMENT_DEFINITIONS') {
      defaultField.source = 'SEGMENT_ID';
    } else if (this.editingModel.source === 'USER_SEGMENTS') {
      defaultField.source = 'SEGMENTS_ADNUNTIUS_USER_ID';
    } else {
      defaultField.source = 'CONSTANT';
    }
    this.fields[this.editingModel.source].push(defaultField);
    this.markExternalFormDirty();
  }

  deleteField(index: number): void {
    _.pullAt(this.fields[this.editingModel.source], index);
    this.markExternalFormDirty();
  }

  isSelected(values: any[], value: any): boolean {
    return Array.isArray(values) && values.indexOf(value) > -1;
  }

  toggleSelection(values: any[], value: any): void {
    if (!Array.isArray(values)) {
      return;
    }
    const index = values.indexOf(value);
    if (index > -1) {
      values.splice(index, 1);
    } else {
      values.push(value);
    }
    this.markExternalFormDirty();
  }

  fieldSummary(field: DataExportField): string {
    if (field.source !== 'CONSTANT') {
      return '';
    }
    return `${field.constantString || field.constantNumber || 'undefined'}`;
  }

  statsExportedThrough(lastSuccessfulExport: Date | string | number): Date | null {
    const exclusiveBoundary = new Date(lastSuccessfulExport);
    if (Number.isNaN(exclusiveBoundary.getTime())) {
      return null;
    }
    return new Date(exclusiveBoundary.getTime() - 1);
  }

  private initializeNestedSettings(): void {
    this.editingModel.sftpSettings = this.editingModel.sftpSettings || {};
    this.editingModel.csvSettings = this.editingModel.csvSettings || {};
    this.editingModel.awsSettings = this.editingModel.awsSettings || {};
    this.editingModel.azureSettings = this.editingModel.azureSettings || {};
    this.editingModel.gcsSettings = this.editingModel.gcsSettings || {};
    this.editingModel.sheetSettings = this.editingModel.sheetSettings || {};
    this.editingModel.statsSettings = this.editingModel.statsSettings || {};
    this.editingModel.eventTypes = this.editingModel.eventTypes || [];
  }

  private initializeScheduleTime(): void {
    if (!this.editingModel.scheduleTime) {
      this.editingModel.modScheduleTime = null;
      return;
    }

    const timeZone = this.LocalNetworkProfile.get('timeZone') || 'utc';
    const scheduleTime = _.isDate(this.editingModel.scheduleTime)
      ? moment(this.editingModel.scheduleTime).format('HH:mm:ss')
      : this.editingModel.scheduleTime;
    this.editingModel.modScheduleTime = pickerDateFromNetworkTime(scheduleTime, timeZone);
  }

  private initializeDefaultsForNewModel(): void {
    this.editingModel.data = this.editingModel.data || 'DELTAS';
    this.editingModel.format = this.editingModel.format || 'CSV';
    this.editingModel.type = this.editingModel.type || (this.app === 'DATA' ? 'SFTP' : 'S3');
    if (this.editingModel.type === 'S3') {
      this.editingModel.awsSettings.region = this.editingModel.awsSettings.region || 'EU_CENTRAL_1';
    }
    this.editingModel.schedule = this.editingModel.schedule || 'DAILY';
    this.editingModel.compression = this.editingModel.compression || 'GZIP';
    if (this.app === 'DATA') {
      this.editingModel.source = this.editingModel.source || 'USER_SEGMENTS';
    } else if (this.perms.MANAGE_SYSTEM === true && this.perms.DOWNLOAD_LOGS !== true) {
      this.editingModel.source = this.editingModel.source || 'STATS';
    } else if (this.perms.AX_ADVERTISER_TEAM_ADMIN===true || this.perms.AX_PUBLISHER_TEAM_ADMIN===true || this.perms.AX_ADVERTISER_ADMIN === true || this.perms.AX_PUBLISHER_ADMIN === true) {
      this.editingModel.source = this.editingModel.source || 'STATS';
    } else {
      this.editingModel.source = this.editingModel.source || 'RAW_LOG';
    }
    this.editingModel.csvSettings.separator = this.editingModel.csvSettings.separator || 'COMMA';
    this.editingModel.csvSettings.arraySeparator = this.editingModel.csvSettings.arraySeparator || 'TAB';
    this.editingModel.csvSettings.header = this.editingModel.csvSettings.header || false;
    this.editingModel.statsSettings.filterType = this.editingModel.statsSettings.filterType || undefined;
    this.editingModel.statsSettings.fields = this.editingModel.statsSettings.fields || this.statsFields.slice();
    this.editingModel.statsSettings.useLineItemCurrency = this.editingModel.statsSettings.useLineItemCurrency || false;
  }

  private updateAllTeams(): void {
    this.lastLoadedStatsFilterType = 'TEAM';
    this.teamsLoaded = false;
    const teamTypes: string[] = [];
    if (this.perms.AX_ADVERTISER_ADMIN === true || this.perms.AX_ADVERTISER_TEAM_ADMIN === true) {
      teamTypes.push('AX_ADVERTISER');
    }
    if (this.perms.AX_PUBLISHER_ADMIN === true || this.perms.AX_PUBLISHER_TEAM_ADMIN === true) {
      teamTypes.push('AX_PUBLISHER');
    }
    const params: any = {minimal: true, teamTypes: teamTypes.join(';')};
    this.teamApi.listOptions(params).subscribe((page) => {
      this.ngZone.run(() => {
        this.allTeams = page.results || [];
        this.teamsLoaded = true;
      });
    });
  }

  private doFields(): void {
    this.fields = {
      SEGMENT_DEFINITIONS: _.get(this.fields, 'SEGMENT_DEFINITIONS') || [],
      USER_SEGMENTS: _.get(this.fields, 'USER_SEGMENTS') || [],
      USER_PROFILES: _.get(this.fields, 'USER_PROFILES') || [],
      RAW_LOG: _.get(this.fields, 'RAW_LOG') || []
    };

    this.fields[this.editingModel.source] = this.editingModel.fields || [];
  }

  private parseExample(data: any): any {
    if (this.editingModel.format !== 'JSON') {
      return data;
    }
    try {
      return typeof data === 'string' ? JSON.parse(data) : data;
    } catch (_error) {
      return data;
    }
  }

  private toIdArray(values: any): any[] {
    return _.compact(_.map(values || [], (value: any) => _.isObject(value) ? _.get(value, 'id') : value));
  }

  private buildStatsFields(): string[] {
    const statsFields = BASE_STATS_FIELDS.slice();
    if (!this.LocalNetworkProfile.isMarketplacePlatform()) {
      _.pull(statsFields, 'Publisher');
    }
    return statsFields;
  }
}
